import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  RefreshCw, AlertTriangle, Info, CheckCircle2, AlertCircle,
  Clock, Send, Mail, Smartphone, ChevronDown, User, Inbox,
  BellRing, X, CheckCheck, BellOff, Search,
} from 'lucide-react';
import { dashboardService } from '@/services/dashboardService';
import { notificationService, type SentNotification, type RecipientType } from '@/services/notificationService';
import { schoolAdminService } from '@/services/schoolAdminService';

// ── helpers ───────────────────────────────────────────────────────────────────

type NotificationLevel = 'critical' | 'warning' | 'info';

interface AdminNotification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  level: NotificationLevel;
  read: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  warning:  'bg-amber-100 text-amber-700 border-amber-200',
  info:     'bg-blue-100 text-blue-700 border-blue-200',
};

function timeAgo(iso: string): string {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'Just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// ── Notification card (matches teacher NotifCard style) ───────────────────────

function NotifCard({ n, dm, onMarkRead }: { n: AdminNotification; dm: boolean; onMarkRead: (id: string) => void }) {
  const urgent = n.level === 'critical' || n.level === 'warning';

  function iconBg() {
    if (n.level === 'critical') return 'bg-red-50';
    if (n.level === 'warning')  return 'bg-amber-50';
    return 'bg-blue-50';
  }

  function NotifIcon() {
    if (n.level === 'critical') return <AlertTriangle className="h-4 w-4 text-red-500" />;
    if (n.level === 'warning')  return <AlertCircle   className="h-4 w-4 text-amber-500" />;
    return <Info className="h-4 w-4 text-blue-500" />;
  }

  return (
    <div className={`border rounded-xl p-4 shadow-sm transition hover:shadow-md ${
      urgent
        ? (dm ? 'border-red-700 ring-1 ring-red-900' : 'border-red-200 ring-1 ring-red-100')
        : (dm ? 'border-slate-700' : 'border-slate-200')
    } ${dm ? 'bg-slate-800' : 'bg-white'} ${n.read ? 'opacity-60' : ''}`}>
      <div className="flex gap-3">
        <div className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full ${iconBg()}`}>
          <NotifIcon />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className={`text-sm font-semibold leading-snug ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{n.title}</p>
            <div className="flex items-center gap-2 shrink-0">
              {!n.read && (
                <span className="text-xs font-semibold text-blue-500">New</span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_COLORS[n.level] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                {n.level}
              </span>
            </div>
          </div>
          <p className={`text-sm mt-0.5 ${dm ? 'text-white' : 'text-slate-600'}`}>{n.message}</p>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Clock className="h-3 w-3" />
              {timeAgo(n.timestamp)}
            </div>
            {!n.read && (
              <button
                onClick={() => onMarkRead(n.id)}
                className="btn-glass btn-glass-pill text-xs"
              >
                Mark read
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Inbox Tab ─────────────────────────────────────────────────────────────────

const LEVEL_PILLS = [
  { key: 'all'      as const, label: 'All'      },
  { key: 'critical' as const, label: 'Critical'  },
  { key: 'warning'  as const, label: 'Warning'   },
  { key: 'info'     as const, label: 'Info'      },
];

function InboxTab({ dm }: { dm: boolean }) {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [activeSection, setActiveSection] = useState<'urgent' | 'recent'>('urgent');
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    schoolAdminService.getNotifications()
      .then(data => { setNotifications(data.notifications || []); setLastUpdated(new Date()); })
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead    = (id: string) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  const markAllAsRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));

  const applyFilters = (list: AdminNotification[]) => list.filter(n => {
    const q = search.toLowerCase();
    const matchesSearch = !q || n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q);
    const matchesLevel  = levelFilter === 'all' || n.level === levelFilter;
    return matchesSearch && matchesLevel;
  });

  const grouped = useMemo(() => ({
    urgent: notifications.filter(n => !n.read && (n.level === 'critical' || n.level === 'warning')),
    recent: notifications.filter(n => n.read  || n.level === 'info'),
  }), [notifications]);

  const visibleUrgent = applyFilters(grouped.urgent);
  const visibleRecent = applyFilters(grouped.recent);

  const unreadCount = notifications.filter(n => !n.read).length;
  const hasFilters  = search.trim() !== '' || levelFilter !== 'all';

  return (
    <div className="space-y-4">
      {/* Top row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className={`text-sm ${dm ? 'text-white' : 'text-slate-500'}`}>
          {lastUpdated
            ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · auto-refreshes every 60s`
            : 'Loading…'}
        </p>
        <div className="flex items-center gap-2" data-dm={dm}>
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
          <button onClick={fetchNotifications} disabled={loading} className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && notifications.length === 0 ? (
        <div className={`flex justify-center items-center py-20 ${dm ? 'text-white' : 'text-slate-400'}`}>
          <RefreshCw className="animate-spin h-5 w-5 mr-2" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : notifications.length === 0 ? (
        <div className={`flex flex-col items-center py-20 gap-3 ${dm ? 'text-white' : 'text-slate-400'}`}>
          <BellOff className="h-10 w-10 opacity-40" />
          <p className="text-sm">No notifications yet</p>
        </div>
      ) : (
        <>
          {/* Section toggle + filters row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Section toggle */}
            <div className={`inline-flex rounded-3xl backdrop-blur-lg border-2 overflow-hidden ${
              dm ? 'bg-slate-900/30 border-slate-600/60 shadow-lg' : 'bg-white/30 border-slate-300/60 shadow-lg'
            }`}>
              {([
                { key: 'urgent' as const, label: 'Requires Attention', icon: AlertCircle, count: grouped.urgent.length },
                { key: 'recent' as const, label: 'Recent Activity',    icon: Info,         count: grouped.recent.length },
              ]).map(({ key, label, icon: Icon, count }) => {
                const isActive = activeSection === key;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    className="flex items-center font-medium px-4 py-1 gap-2"
                    style={{
                      background: isActive
                        ? (key === 'urgent' ? '#ef4444' : (dm ? '#475569' : '#1e293b'))
                        : 'transparent',
                      color: isActive ? 'white' : (dm ? '#94a3b8' : '#64748b'),
                      border: 'none',
                      borderRadius: key === 'urgent' ? '9999px 0 0 9999px' : '0 9999px 9999px 0',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isActive ? '600' : '500',
                    }}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {label}
                    {count > 0 && (
                      <span
                        className="px-1.5 py-0.5 rounded-full text-xs font-bold tabular-nums"
                        style={{
                          background: isActive
                            ? 'rgba(255,255,255,0.2)'
                            : (key === 'urgent' ? '#fee2e2' : (dm ? '#1e293b' : '#e2e8f0')),
                          color: isActive ? 'white' : (key === 'urgent' ? '#dc2626' : (dm ? '#94a3b8' : '#64748b')),
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Search input */}
            <div className="relative flex-1 min-w-[160px] max-w-xs">
              <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none ${dm ? 'text-white' : 'text-slate-400'}`} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search notifications…"
                className={`w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border outline-none ${
                  dm
                    ? 'bg-slate-700/60 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-slate-400'
                    : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-slate-400'
                }`}
              />
            </div>

            {/* Level filter pills */}
            <div className="flex items-center gap-1">
              {LEVEL_PILLS.map(({ key, label }) => {
                const isActive = levelFilter === key;
                const activeColors: Record<string, { bg: string; border: string }> = {
                  all:      { bg: dm ? '#475569' : '#1e293b', border: 'transparent' },
                  critical: { bg: '#ef4444',                  border: 'transparent' },
                  warning:  { bg: '#f59e0b',                  border: 'transparent' },
                  info:     { bg: '#3b82f6',                  border: 'transparent' },
                };
                return (
                  <button
                    key={key}
                    onClick={() => setLevelFilter(key)}
                    className="text-xs px-3 py-1 rounded-full font-medium transition-all"
                    style={{
                      appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer',
                      background: isActive ? activeColors[key].bg : 'transparent',
                      border: `1px solid ${isActive ? activeColors[key].border : (dm ? '#475569' : '#cbd5e1')}`,
                      color: isActive ? '#ffffff' : (dm ? '#94a3b8' : '#64748b'),
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cards */}
          <div className="space-y-2">
            {activeSection === 'urgent' ? (
              visibleUrgent.length === 0 ? (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${
                  dm ? 'border-slate-700 bg-slate-800 text-slate-500' : 'border-slate-100 bg-slate-50 text-slate-400'
                }`}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  {hasFilters ? 'No results match your filters' : 'All clear — no alerts require attention'}
                </div>
              ) : visibleUrgent.map(n => (
                <NotifCard key={n.id} n={n} dm={dm} onMarkRead={markAsRead} />
              ))
            ) : (
              visibleRecent.length === 0 ? (
                <div className={`px-4 py-3 rounded-xl border text-sm ${
                  dm ? 'border-slate-700 bg-slate-800 text-slate-500' : 'border-slate-100 bg-slate-50 text-slate-400'
                }`}>
                  {hasFilters ? 'No results match your filters' : 'No recent activity'}
                </div>
              ) : visibleRecent.map(n => (
                <NotifCard key={n.id} n={n} dm={dm} onMarkRead={markAsRead} />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Compose Tab ───────────────────────────────────────────────────────────────

interface PersonOption { id: string; name: string; email: string; }

function ComposeTab({ dm }: { dm: boolean }) {
  const [students, setStudents]             = useState<PersonOption[]>([]);
  const [teachers, setTeachers]             = useState<PersonOption[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [search, setSearch]                 = useState('');
  const [dropdownOpen, setDropdownOpen]     = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);
  const [recipientType, setRecipientType]   = useState<RecipientType>('student_push');
  const [title, setTitle]                   = useState('');
  const [message, setMessage]               = useState('');
  const [sending, setSending]               = useState(false);
  const [result, setResult]                 = useState<{ success: boolean; detail: string } | null>(null);
  const [sentHistory, setSentHistory]       = useState<SentNotification[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<SentNotification | null>(null);
  const [historySearch, setHistorySearch]   = useState('');
  const [viaFilter, setViaFilter]           = useState<'all' | 'push' | 'email'>('all');

  const isTeacherMode = recipientType === 'teacher_push';

  useEffect(() => {
    dashboardService.getMonitoredStudents(1, 100)
      .then(res => setStudents(res.data.map(s => ({ id: s.id, name: s.name, email: s.email }))))
      .catch(() => {}).finally(() => setStudentsLoading(false));

    fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/dashboard/school-admin/users`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('authToken')}` },
    })
      .then(r => r.ok ? r.json() : { users: [] })
      .then(data => setTeachers((data.users || []).filter((u: PersonOption & { role?: string }) => u.role === 'teacher')))
      .catch(() => {}).finally(() => setTeachersLoading(false));

    notificationService.getSent()
      .then(setSentHistory).catch(() => {}).finally(() => setHistoryLoading(false));
  }, []);

  const switchType = (t: RecipientType) => { setRecipientType(t); setSelectedPerson(null); setSearch(''); setDropdownOpen(false); };

  const list        = isTeacherMode ? teachers : students;
  const listLoading = isTeacherMode ? teachersLoading : studentsLoading;
  const filtered    = list.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleSend = async () => {
    if (!selectedPerson || !title.trim() || !message.trim()) return;
    setSending(true); setResult(null);
    try {
      const res = await notificationService.send({
        recipientType,
        studentId: !isTeacherMode ? selectedPerson.id : undefined,
        teacherId:  isTeacherMode ? selectedPerson.id : undefined,
        title: title.trim(),
        message: message.trim(),
      });
      setResult({ success: res.success && res.delivered, detail: res.detail });
      if (res.success) {
        setSentHistory(prev => [{
          id: Date.now().toString(),
          title: title.trim(), message: message.trim(),
          sentVia: recipientType === 'parent_email' ? 'email' : 'push',
          status: 'read', createdAt: new Date().toISOString(),
          recipient: { name: selectedPerson.name, email: selectedPerson.email },
        }, ...prev]);
        setTitle(''); setMessage(''); setSelectedPerson(null); setSearch('');
      }
    } catch (err: unknown) {
      setResult({ success: false, detail: err instanceof Error ? err.message : 'Network error' });
    } finally { setSending(false); }
  };

  const canSend    = !!selectedPerson && title.trim().length > 0 && message.trim().length > 0 && !sending;
  const inputCls   = dm ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-200 text-slate-800 placeholder-slate-400';

  const TYPES = [
    { value: 'student_push' as const, label: 'Push to Student', icon: Smartphone },
    { value: 'parent_email' as const, label: 'Email to Parent', icon: Mail },
    { value: 'teacher_push' as const, label: 'Push to Teacher', icon: BellRing },
  ];

  return (
    <div className="space-y-6">
      {/* Compose form */}
      <div className={`border rounded-xl p-5 shadow-sm space-y-4 ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <h3 className={`text-sm font-semibold ${dm ? 'text-slate-200' : 'text-slate-700'}`}>New Notification</h3>

        {/* Recipient type — pill toggle */}
        <div className="flex gap-2 flex-wrap" data-dm={dm}>
          {TYPES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => switchType(value)}
              className={`btn-glass btn-glass-pill flex items-center gap-2 text-sm ${recipientType === value ? 'btn-glass-blue' : ''}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Recipient picker */}
        <div className="relative">
          <label className={`text-xs font-medium mb-1 block ${dm ? 'text-white' : 'text-slate-500'}`}>
            {recipientType === 'student_push' ? 'Student' : recipientType === 'parent_email' ? 'Student (parent email will be used)' : 'Teacher'}
          </label>
          <button
            onClick={() => setDropdownOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm text-left transition"
            style={{
              appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer',
              background: dm ? 'rgba(30,41,59,0.7)' : 'rgba(241,245,249,0.9)',
              borderColor: dm ? 'rgba(71,85,105,0.7)' : 'rgba(203,213,225,0.8)',
              color: dm ? '#e2e8f0' : '#1e293b',
            }}
          >
            <span style={{ color: selectedPerson ? (dm ? '#e2e8f0' : '#334155') : '#94a3b8' }}>
              {selectedPerson
                ? <span className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-slate-400" />{selectedPerson.name}</span>
                : listLoading ? `Loading ${isTeacherMode ? 'teachers' : 'students'}…` : `Select a ${isTeacherMode ? 'teacher' : 'student'}…`}
            </span>
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
          </button>
          {dropdownOpen && (
            <div className={`absolute z-20 mt-1 w-full rounded-xl shadow-lg overflow-hidden border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <div className={`p-2 border-b ${dm ? 'border-slate-700' : 'border-slate-100'}`}>
                <input autoFocus type="text" placeholder="Search…" value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={`w-full text-sm px-3 py-1.5 border rounded-lg outline-none ${dm ? 'bg-slate-700/50 border-slate-600/50 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-200 text-slate-600 placeholder-slate-400'}`}
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filtered.length === 0
                  ? <p className={`px-3 py-3 text-xs ${dm ? 'text-white' : 'text-slate-400'}`}>No {isTeacherMode ? 'teachers' : 'students'} found</p>
                  : filtered.map(p => (
                    <button key={p.id} onClick={() => { setSelectedPerson(p); setDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm transition ${dm ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <p className={`font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{p.name}</p>
                      <p className={`text-xs ${dm ? 'text-white' : 'text-slate-400'}`}>{p.email}</p>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Title */}
        <div>
          <label className={`text-xs font-medium mb-1 block ${dm ? 'text-white' : 'text-slate-500'}`}>Subject / Title</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={120}
            placeholder={recipientType === 'parent_email' ? 'Safety concern regarding your child' : recipientType === 'teacher_push' ? 'Heads-up from school administration' : 'Message from school administration'}
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 ${inputCls}`}
          />
        </div>

        {/* Message */}
        <div>
          <label className={`text-xs font-medium mb-1 block ${dm ? 'text-white' : 'text-slate-500'}`}>Message</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} maxLength={1000}
            placeholder="Write your message here…"
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-none ${inputCls}`}
          />
          <p className={`text-xs text-right mt-0.5 ${dm ? 'text-white' : 'text-slate-400'}`}>{message.length}/1000</p>
        </div>

        {/* Result banner */}
        {result && (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
            result.success
              ? (dm ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-700' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
              : (dm ? 'bg-red-900/40 text-red-400 border border-red-700' : 'bg-red-50 text-red-700 border border-red-200')
          }`}>
            {result.success ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
            {result.detail}
          </div>
        )}

        {/* Send button — matches teacher blue style */}
        <button onClick={handleSend} disabled={!canSend}
          className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2 disabled:opacity-40 font-semibold"
        >
          <Send className="h-4 w-4" />
          {sending ? 'Sending…' : recipientType === 'parent_email' ? 'Send Email to Parent' : recipientType === 'teacher_push' ? 'Send Push to Teacher' : 'Send Push to Student'}
        </button>
      </div>

      {/* Sent history — split panel matching teacher */}
      <div className={`flex gap-4`}>
        <div className={`${selectedNotification ? 'w-1/2' : 'w-full'}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? 'text-white' : 'text-slate-600'}`}>
              <Clock className="h-4 w-4 text-slate-400" /> Sent History
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Via filter pills */}
              <div className="flex items-center gap-1" data-dm={dm}>
                {([
                  { key: 'all'   as const, label: 'All'   },
                  { key: 'push'  as const, label: 'Push'  },
                  { key: 'email' as const, label: 'Email' },
                ]).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setViaFilter(key)}
                    className={`btn-glass btn-glass-pill text-xs ${viaFilter === key ? 'btn-glass-blue' : ''}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* Search input */}
              <div className="relative">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none ${dm ? 'text-white' : 'text-slate-400'}`} />
                <input
                  type="text"
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                  placeholder="Search sent…"
                  className={`pl-8 pr-3 py-1.5 text-sm rounded-lg border outline-none w-40 ${
                    dm
                      ? 'bg-slate-700/60 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-slate-400'
                      : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-slate-400'
                  }`}
                />
              </div>
            </div>
          </div>
          {historyLoading ? (
            <p className={`text-sm text-center py-8 ${dm ? 'text-white' : 'text-slate-400'}`}>Loading…</p>
          ) : sentHistory.length === 0 ? (
            <p className={`text-sm text-center py-8 ${dm ? 'text-white' : 'text-slate-400'}`}>No notifications sent yet.</p>
          ) : (
            <div className={`divide-y ${dm ? 'divide-slate-700' : 'divide-slate-200'}`}>
              {sentHistory.filter(n => {
                const q = historySearch.toLowerCase();
                const matchesSearch = !q || n.title.toLowerCase().includes(q) || (n.recipient?.name || '').toLowerCase().includes(q) || n.message.toLowerCase().includes(q);
                const matchesVia = viaFilter === 'all' || n.sentVia === viaFilter;
                return matchesSearch && matchesVia;
              }).map(n => (
                <div key={n.id} onClick={() => setSelectedNotification(n)}
                  className={`py-3 px-2 cursor-pointer transition rounded-lg ${
                    selectedNotification?.id === n.id
                      ? (dm ? 'bg-blue-900/20 border-l-2 border-blue-500' : 'bg-blue-50/50 border-l-2 border-blue-500')
                      : (dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50')
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {n.sentVia === 'push'
                        ? <Smartphone className="h-4 w-4 text-blue-500 shrink-0" />
                        : <Mail className="h-4 w-4 text-emerald-500 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold truncate ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{n.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {n.recipient && <p className={`text-xs ${dm ? 'text-white' : 'text-slate-400'}`}>To: {n.recipient.name}</p>}
                          <span className={`text-xs ${dm ? 'text-white' : 'text-slate-400'}`}>·</span>
                          <p className={`text-xs ${dm ? 'text-white' : 'text-slate-400'}`}>{timeAgo(n.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className={`mt-1.5 text-xs line-clamp-1 pl-6 ${dm ? 'text-white' : 'text-slate-600'}`}>{n.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedNotification && <div className={`w-px ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />}

        {selectedNotification && (
          <div className={`w-1/2 flex flex-col h-fit overflow-hidden mt-8 rounded-xl border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`px-5 py-4 border-b ${dm ? 'border-slate-700 bg-slate-800/50' : 'border-slate-100 bg-slate-50/50'}`}>
              <div className="flex items-start justify-between mb-3">
                <h3 className={`text-base font-semibold leading-tight ${dm ? 'text-slate-100' : 'text-slate-900'}`}>{selectedNotification.title}</h3>
                <button onClick={() => setSelectedNotification(null)}
                  style={{ appearance: 'none', WebkitAppearance: 'none', border: 'none', background: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: dm ? '#94a3b8' : '#64748b' }}
                  onMouseEnter={e => { e.currentTarget.style.background = dm ? '#334155' : '#f1f5f9'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${dm ? 'bg-blue-600' : 'bg-blue-500'} text-white text-xs font-semibold`}>A</div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${dm ? 'text-slate-200' : 'text-slate-800'}`}>School Admin (You)</p>
                  <p className={`text-xs ${dm ? 'text-white' : 'text-slate-500'}`}>
                    to {selectedNotification.recipient?.name} {'<'}{selectedNotification.recipient?.email}{'>'}
                  </p>
                </div>
                <span className={`text-xs shrink-0 ${dm ? 'text-white' : 'text-slate-400'}`}>
                  {new Date(selectedNotification.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
            </div>
            <div className={`flex-1 p-5 min-h-[160px] ${dm ? 'bg-slate-800' : 'bg-white'}`}>
              <p className={`whitespace-pre-wrap leading-relaxed text-sm ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{selectedNotification.message}</p>
            </div>
            <div className={`px-5 py-3 border-t text-xs flex items-center gap-2 ${dm ? 'border-slate-700 bg-slate-800/50 text-slate-500' : 'border-slate-100 bg-slate-50/50 text-slate-500'}`}>
              {selectedNotification.sentVia === 'push'
                ? <><Smartphone className="h-3.5 w-3.5" /> Sent via Push Notification</>
                : <><Mail className="h-3.5 w-3.5" /> Sent via Email</>}
              <span className="mx-1">·</span>
              Delivered successfully
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export function SchoolAdminNotifications({ darkMode: dm = false }: { darkMode?: boolean }) {
  const [tab, setTab] = useState<'inbox' | 'compose'>('inbox');

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header + tabs — matches teacher exactly */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Notifications</h2>
        <div className="flex gap-2" data-dm={dm}>
          {([
            { key: 'inbox'   as const, label: 'Inbox',   icon: Inbox },
            { key: 'compose' as const, label: 'Compose', icon: Send  },
          ]).map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`btn-glass btn-glass-pill flex items-center gap-2 text-sm ${tab === key ? 'btn-glass-blue' : ''}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'inbox' ? <InboxTab dm={dm} /> : <ComposeTab dm={dm} />}
    </div>
  );
}
